// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { useCallback, useInsertionEffect, useMemo, useRef, useState, useSyncExternalStore, type SetStateAction } from "react";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";

// Wingo UI Pro · https://wingo-ui.com/legal/license

// where a value lives. cookie is the one the server can read too (a layout reads it and passes defaultValue, so a
// collapsed sidebar renders collapsed on the first paint instead of jumping after hydration)
export type PersistedStorage = "local" | "session" | "cookie";

export type UsePersistedStateOptions<T> = {
  // default "local"
  storage?: PersistedStorage;
  // how the value becomes a string and back. default JSON; a value that fails to parse falls back to defaultValue
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
  // cookie lifetime in seconds (default one year)
  maxAge?: number;
  // follow changes made in other tabs (local storage only, default true). instances on the same page always sync
  syncTabs?: boolean;
};

export type PersistedStateMeta = {
  // false on the server and during hydration (the value is defaultValue then), true once storage has been read.
  // components that animate a change can skip the animation for the restore that happens right after hydration
  hydrated: boolean;
  // forgets the stored value; the state returns to defaultValue
  remove: () => void;
};

const YEAR = 60 * 60 * 24 * 365;

// storage that threw (private mode, quota, blocked cookies) keeps working in memory for the rest of the session
const memory = new Map<string, string | null>();
const listeners = new Map<string, Set<() => void>>();

// the cookie name for a key: letters, digits, dot, dash and underscore only. read it on the server with
// cookies().get(persistedCookieName(key))?.value and JSON.parse(decodeURIComponent(value))
export function persistedCookieName(key: string) {
  return key.replace(/[^A-Za-z0-9._-]/g, "_");
}

function slotId(storage: PersistedStorage, key: string) {
  return `${storage}:${key}`;
}

function webStorage(storage: PersistedStorage): Storage | null {
  if (typeof window === "undefined") return null;
  return storage === "session" ? window.sessionStorage : window.localStorage;
}

function readCookie(name: string): string | null {
  for (const part of document.cookie ? document.cookie.split("; ") : []) {
    const eq = part.indexOf("=");
    if (part.slice(0, eq) === name) {
      try {
        return decodeURIComponent(part.slice(eq + 1));
      } catch {
        return null;
      }
    }
  }
  return null;
}

// the raw stored string, or null when nothing is stored. safe outside react; null on the server
export function readPersisted(key: string, storage: PersistedStorage = "local"): string | null {
  if (typeof window === "undefined") return null;
  const id = slotId(storage, key);
  if (memory.has(id)) return memory.get(id) ?? null;
  try {
    if (storage === "cookie") return readCookie(persistedCookieName(key));
    return webStorage(storage)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

// writes (or with null removes) a raw string and tells every hook reading the same key. safe outside react
export function writePersisted(key: string, raw: string | null, storage: PersistedStorage = "local", maxAge = YEAR) {
  if (typeof window === "undefined") return;
  const id = slotId(storage, key);
  try {
    if (storage === "cookie") {
      const name = persistedCookieName(key);
      document.cookie =
        raw === null
          ? `${name}=; path=/; max-age=0; samesite=lax`
          : `${name}=${encodeURIComponent(raw)}; path=/; max-age=${Math.round(maxAge)}; samesite=lax`;
    } else {
      const store = webStorage(storage);
      if (raw === null) store?.removeItem(key);
      else store?.setItem(key, raw);
    }
    memory.delete(id);
  } catch {
    memory.set(id, raw);
  }
  listeners.get(id)?.forEach((notify) => notify());
}

function subscribeSlot(id: string, notify: () => void) {
  let set = listeners.get(id);
  if (!set) {
    set = new Set();
    listeners.set(id, set);
  }
  set.add(notify);
  return () => {
    set.delete(notify);
    if (!set.size) listeners.delete(id);
  };
}

const subscribeNever = () => () => {};

function defaultDeserialize<T>(raw: string): T {
  return JSON.parse(raw) as T;
}

// useState that survives reloads: usePersistedState("inbox:view", "list"). the server and the hydration render see
// defaultValue, then the stored value (no hydration mismatch). every instance with the same key and storage stays in
// sync, other tabs too. a null / empty key turns persistence off, so a component can take an optional persistKey
export function usePersistedState<T>(
  key: string | null | undefined,
  defaultValue: T,
  { storage = "local", serialize = JSON.stringify, deserialize = defaultDeserialize, maxAge = YEAR, syncTabs = true }: UsePersistedStateOptions<T> = {},
): [T, (next: SetStateAction<T>) => void, PersistedStateMeta] {
  const [local, setLocal] = useState(defaultValue);

  const subscribe = useCallback(
    (notify: () => void) => {
      if (!key) return () => {};
      const unsubscribe = subscribeSlot(slotId(storage, key), notify);
      if (!syncTabs || storage !== "local") return unsubscribe;
      const onStorage = (event: StorageEvent) => {
        if (event.key === key && event.storageArea === window.localStorage) notify();
      };
      window.addEventListener("storage", onStorage);
      return () => {
        unsubscribe();
        window.removeEventListener("storage", onStorage);
      };
    },
    [key, storage, syncTabs],
  );

  // the raw string is the snapshot (a primitive, so react compares it cheaply); parsing is memoized per string
  const raw = useSyncExternalStore(
    subscribe,
    () => (key ? readPersisted(key, storage) : null),
    () => null,
  );
  const hydrated = useSyncExternalStore(subscribeNever, () => true, () => false);

  // the latest parse helpers and default, read by the memo and the stable setter
  const latest = useRef({ serialize, deserialize, defaultValue });
  useInsertionEffect(() => {
    latest.current = { serialize, deserialize, defaultValue };
  });

  const stored = useMemo(() => {
    if (raw === null) return undefined;
    try {
      return { value: deserialize(raw) };
    } catch {
      return undefined;
    }
  }, [raw, deserialize]);

  const value = key ? (stored ? stored.value : defaultValue) : local;

  const current = useRef(value);
  useInsertionEffect(() => {
    current.current = value;
  });

  const setValue = useCallback(
    (next: SetStateAction<T>) => {
      if (!key) {
        setLocal(next);
        return;
      }
      const resolved = typeof next === "function" ? (next as (prev: T) => T)(current.current) : next;
      if (Object.is(resolved, current.current)) return;
      // updaters called twice in one event build on each other
      current.current = resolved;
      writePersisted(key, latest.current.serialize(resolved), storage, maxAge);
    },
    [key, storage, maxAge],
  );

  const remove = useCallback(() => {
    if (key) writePersisted(key, null, storage, maxAge);
    else setLocal(latest.current.defaultValue);
  }, [key, storage, maxAge]);

  const meta = useMemo<PersistedStateMeta>(() => ({ hydrated, remove }), [hydrated, remove]);
  return [value, setValue, meta];
}

export type UsePersistedControllableStateParams<T> = UsePersistedStateOptions<T> & {
  // storage key; null / empty keeps the state in memory only
  key: string | null | undefined;
  // pass it to control the state from outside (then nothing is written to storage)
  value?: T;
  defaultValue: T;
  onChange?: (value: T) => void;
};

// the controlled / uncontrolled pattern of useControllableState, where the uncontrolled value is persisted:
//   const [collapsed, setCollapsed] = usePersistedControllableState({ key: persistKey, value: collapsedProp,
//     defaultValue: defaultCollapsed, onChange: onCollapsedChange, storage: "cookie" })
export function usePersistedControllableState<T>({
  key,
  value,
  defaultValue,
  onChange,
  ...options
}: UsePersistedControllableStateParams<T>): [T, (next: SetStateAction<T>) => void, PersistedStateMeta] {
  const persist = Boolean(key) && value === undefined;
  const [stored, setStored, meta] = usePersistedState(persist ? key : null, defaultValue, options);
  const [state, setState] = useControllableState<T>({
    value: value !== undefined ? value : persist ? stored : undefined,
    defaultValue,
    onChange: (next) => {
      if (persist) setStored(next);
      onChange?.(next);
    },
  });
  return [state, setState, meta];
}
